/**
 * Notice Field
 * 
 * Displays informational content, URLs, or code snippets
 * Used for chat URLs and embed codes
 */

import React, { useState } from 'react';
import type { InferredPropertySchema } from '@/types/inferred-schema';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, Check, ExternalLink, Code } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useGetBackendUrl } from '@/hooks/use-get-backend-url';
import { chatApi } from '@/lib/api';
import { getChatUrl } from '@/utils/environment';

interface NoticeFieldProps {
  property: InferredPropertySchema;
  value: any;
  onChange: (value: any) => void;
  error?: string;
  nodeId?: string;
  nodes?: any[];
}

export function NoticeField({
  property,
  value,
  onChange,
  error,
  nodeId,
  nodes,
}: NoticeFieldProps) {
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [embedCode, setEmbedCode] = useState<string>('');
  const [htmlExample, setHtmlExample] = useState<string>('');
  const [chatUrl, setChatUrl] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('embed');
  
  const backendUrl = useGetBackendUrl();
  const currentWorkflow = useWorkflowStore((state) => state.currentWorkflow);
  const workflow = currentWorkflow;
  
  // Try to get workflow ID from URL if not in store
  const getWorkflowId = (): string | null => {
    if (workflow?.id) return workflow.id;
    
    // Try to extract from URL (e.g., /workflow/wf_abc123 or /workflows/wf_abc123)
    const match = window.location.pathname.match(/\/workflows?\/([^\/]+)/);
    return match ? match[1] : null;
  };

  // Determine what to display based on property key
  const isEmbedCode = property.key === 'embedCode';
  const isChatUrl = property.key === 'chatUrl';
  const isWebhookUrl = property.key === 'webhookUrl';
  const isMcpServerUrl = property.key === 'mcpServerUrl';
  const isMcpTestBody = property.key === 'mcpTestBody';
  const isClaudeDesktopConfig = property.key === 'claudeDesktopConfig';
  const isE2eBridgeConfig = property.key === 'e2eBridgeConfig';

  // Fetch embed code when component mounts or workflow changes
  React.useEffect(() => {
    const workflowId = getWorkflowId();
    // console.log('🔍 NoticeField effect:', { 
    //   workflowId, 
    //   isEmbedCode, 
    //   isChatUrl,
    //   isWebhookUrl,
    //   propertyKey: property.key,
    //   workflowFromStore: workflow?.id,
    //   pathname: window.location.pathname
    // });
    
    if (isEmbedCode && workflowId) {
      fetchEmbedCode();
    }
    if (isChatUrl && workflowId) {
      generateChatUrl();
    }
    if (isWebhookUrl && workflowId) {
      generateChatUrl();
    }
  }, [workflow?.id, isEmbedCode, isChatUrl, isWebhookUrl]);

  const fetchEmbedCode = async () => {
    const workflowId = getWorkflowId();
    if (!workflowId) return;
    
    setLoading(true);
    try {
      const response = await chatApi.getEmbedCode(workflowId);
      const data = response.data;
      
      if (data.success) {
        setEmbedCode(data.embedCode);
        setHtmlExample(data.htmlExample);
      }
    } catch (error) {
      // console.error('Failed to fetch embed code:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateChatUrl = () => {
    const workflowId = getWorkflowId();
    if (!workflowId) {
      return;
    }
    
    // Use centralized environment utility to generate chat URL
    const url = getChatUrl(workflowId, 'hosted');
    setChatUrl(url);
  };

  const handleCopy = async (text: string) => {
    if (!text) {
      toast.error('Nothing to copy');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleOpenUrl = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Render chat URL notice (Hosted Chat mode)
  if (isChatUrl) {
    return (
      <div className="space-y-2">
        <Label>{property.label}</Label>
        <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium mb-1">Hosted Chat URL</p>
              <div className="text-xs text-muted-foreground break-words overflow-wrap-anywhere">
                {chatUrl || 'Activate workflow to generate URL'}
              </div>
            </div>
          </div>
          
          {chatUrl && (
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleCopy(chatUrl)}
                className="flex-1"
              >
                {copied ? (
                  <>
                    <Check className="h-3 w-3 mr-1" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3 mr-1" />
                    Copy URL
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleOpenUrl(chatUrl)}
                className="flex-1"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Open Chat
              </Button>
            </div>
          )}
          
          {property.description && (
            <p className="text-xs text-muted-foreground mt-2">
              {property.description}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Render webhook URL notice
  if (isWebhookUrl) {
    // Generate webhook URL using workflowId and nodeId
    const workflowId = getWorkflowId();

    // Check if the current node has a custom path configured
    const currentNode = nodes?.find(n => n.id === nodeId);
    const customPath = currentNode?.data?.parameters?.path;

    // Use custom path format if path is set, otherwise use auto-generated format
    let webhookUrl = '';
    if (workflowId && nodeId) {
      if (customPath && customPath.trim()) {
        // Custom path: /custom/{path} (strip leading slash if present)
        const normalizedPath = customPath.replace(/^\//, '');
        webhookUrl = `${backendUrl}/custom/${normalizedPath}`;
      } else {
        // Auto-generated: /api/webhooks/{workflowId}/{nodeId}
        webhookUrl = `${backendUrl}/api/webhooks/${workflowId}/${nodeId}`;
      }
    }

    return (
      <div className="space-y-1.5">
        <Label>{property.label}</Label>
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0 rounded-md border bg-muted/50 px-3 py-2">
            <div className="text-xs text-muted-foreground break-all font-mono">
              {webhookUrl || 'Save workflow to generate URL'}
            </div>
          </div>
          {webhookUrl && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => handleCopy(webhookUrl)}
              className="h-9 w-9 shrink-0"
            >
              {copied ? (
                <Check className="h-4 w-4" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Render MCP Server URL notice
  if (isMcpServerUrl) {
    const workflowId = getWorkflowId();
    const currentNode = nodes?.find(n => n.id === nodeId);
    const transportType = currentNode?.data?.parameters?.transportType || 'sse';

    // POST endpoint for tool calls (same for both transport types)
    const mcpPostUrl = workflowId ? `${backendUrl}/mcp/${workflowId}` : '';
    // SSE endpoint for streaming connections (only for SSE transport)
    const mcpSseUrl = workflowId && transportType === 'sse' ? `${backendUrl}/mcp/${workflowId}/sse` : '';

    return (
      <div className="space-y-2">
        <Label>{property.label}</Label>
        <div className="rounded-lg border bg-muted/50 p-3 space-y-3">
          {/* POST endpoint */}
          <div className="space-y-1">
            <p className="text-xs font-medium text-foreground">POST Endpoint (for tool calls)</p>
            <div className="text-xs text-muted-foreground break-words overflow-wrap-anywhere font-mono bg-background rounded p-2 border">
              {mcpPostUrl || 'Activate workflow to generate URL'}
            </div>
            {mcpPostUrl && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleCopy(mcpPostUrl)}
                className="w-full"
              >
                {copied ? (
                  <>
                    <Check className="h-3 w-3 mr-1" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3 mr-1" />
                    Copy URL
                  </>
                )}
              </Button>
            )}
          </div>

          {/* SSE endpoint (only for SSE transport) */}
          {mcpSseUrl && (
            <div className="space-y-1 pt-2 border-t">
              <p className="text-xs font-medium text-foreground">SSE Endpoint (GET for streaming)</p>
              <div className="text-xs text-muted-foreground break-words overflow-wrap-anywhere font-mono bg-background rounded p-2 border">
                {mcpSseUrl}
              </div>
              <p className="text-xs text-muted-foreground italic">
                Use GET request to open a streaming connection (MCP clients)
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render MCP Test Body notice
  if (isMcpTestBody) {
    const currentNode = nodes?.find(n => n.id === nodeId);
    const toolName = currentNode?.data?.parameters?.toolName || 'my_workflow_tool';
    const testInput = currentNode?.data?.parameters?.testInput;

    // Generate test body using testInput field
    const generateTestBody = () => {
      let testArguments: Record<string, any> = { query: 'test query' };

      if (testInput) {
        try {
          testArguments = typeof testInput === 'string' ? JSON.parse(testInput) : testInput;
        } catch {
          // Keep default if parsing fails
        }
      }

      return JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method: 'tools/call',
        params: {
          name: toolName,
          arguments: testArguments
        }
      }, null, 2);
    };

    const testBody = generateTestBody();

    return (
      <div className="space-y-2">
        <Label>{property.label}</Label>
        <div className="rounded-lg border bg-muted/50 p-3 space-y-2">
          <p className="text-xs text-muted-foreground">
            Use this JSON-RPC 2.0 body to test in Postman or curl:
          </p>
          <pre className="text-xs text-muted-foreground break-words overflow-wrap-anywhere font-mono bg-background rounded p-2 border whitespace-pre-wrap max-h-48 overflow-y-auto">
            {testBody}
          </pre>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleCopy(testBody)}
            className="w-full"
          >
            {copied ? (
              <>
                <Check className="h-3 w-3 mr-1" />
                Copied!
              </>
            ) : (
              <>
                <Code className="h-3 w-3 mr-1" />
                Copy Test Body
              </>
            )}
          </Button>
          <p className="text-xs text-muted-foreground italic">
            Edit "Test Input" on Parameters tab to customize the arguments
          </p>
        </div>
      </div>
    );
  }

  // Render e2e Bridge Configuration notice
  if (isE2eBridgeConfig) {
    const workflowId = getWorkflowId();
    const currentNode = nodes?.find(n => n.id === nodeId);
    
    // Debug logging
    // console.log('🔍 MCP Trigger Node Debug:', {
    //   nodeId,
    //   currentNode,
    //   parameters: currentNode?.data?.parameters,
    //   dataLevel: currentNode?.data,
    //   nodesAvailable: !!nodes,
    //   nodesCount: nodes?.length
    // });
    
    // Try to get toolName and toolDescription from parameters first, then fall back to data level (for old nodes)
    const toolName = currentNode?.data?.parameters?.toolName || 
                     (currentNode?.data as any)?.toolName || 
                     'your_tool_name';
    const toolDescription = currentNode?.data?.parameters?.toolDescription || 
                           (currentNode?.data as any)?.toolDescription || 
                           'Description of what this tool does';
    
    let e2eBridgeConfig = '';
    
    if (workflowId) {
      const bridgeConfig = {
        apiBaseUrl: backendUrl,
        servers: [
          {
            workflowId: workflowId,
            toolName: toolName,
            toolDescription: toolDescription,
            enabled: true,
            customName: null
          }
        ],
        requestTimeout: 60,
        retryAttempts: 3,
        retryDelay: 1000
      };
      
      e2eBridgeConfig = JSON.stringify(bridgeConfig, null, 2);
    }
    
    return (
      <div className="space-y-2">
        <Label>{property.label}</Label>
        <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <pre className="text-xs text-muted-foreground break-words overflow-wrap-anywhere font-mono whitespace-pre-wrap">
                {e2eBridgeConfig || 'Activate workflow to generate configuration'}
              </pre>
            </div>
          </div>
          
          {e2eBridgeConfig && (
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleCopy(e2eBridgeConfig)}
                className="flex-1"
              >
                {copied ? (
                  <>
                    <Check className="h-3 w-3 mr-1" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3 mr-1" />
                    Copy Config
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }



  // Render embed code notice
  if (isEmbedCode) {
    // Get the current content to copy based on active tab
    const getCurrentContent = () => {
      return activeTab === 'embed' ? embedCode : htmlExample;
    };
    
    return (
      <div className="space-y-2">
        <Label>{property.label}</Label>
        <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
          {loading ? (
            <div className="text-sm text-muted-foreground">Loading embed code...</div>
          ) : embedCode ? (
            <>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="embed">Embed Code</TabsTrigger>
                  <TabsTrigger value="html">HTML Example</TabsTrigger>
                </TabsList>
                
                <TabsContent value="embed" className="mt-3">
                  <div className="relative max-h-64 overflow-y-auto">
                    <pre className="text-xs bg-background rounded p-3 border break-words whitespace-pre-wrap overflow-wrap-anywhere">
                      <code>{embedCode}</code>
                    </pre>
                  </div>
                </TabsContent>
                
                <TabsContent value="html" className="mt-3">
                  <div className="relative max-h-64 overflow-y-auto">
                    <pre className="text-xs bg-background rounded p-3 border break-words whitespace-pre-wrap overflow-wrap-anywhere">
                      <code>{htmlExample}</code>
                    </pre>
                  </div>
                </TabsContent>
              </Tabs>
              
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleCopy(getCurrentContent())}
                className="w-full"
              >
                {copied ? (
                  <>
                    <Check className="h-3 w-3 mr-1" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3 mr-1" />
                    Copy Code
                  </>
                )}
              </Button>
            </>
          ) : (
            <div className="text-sm text-muted-foreground">
              Save and activate workflow to generate embed code
            </div>
          )}
          
          {property.description && (
            <p className="text-xs text-muted-foreground mt-2">
              {property.description}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Generic notice display
  return (
    <div className="space-y-2">
      {property.label && <Label>{property.label}</Label>}
      <div className={cn(
        "rounded-lg border p-4",
        property.default?.includes('warning') || property.default?.includes('⚠️') 
          ? "bg-yellow-50 border-yellow-200 dark:bg-yellow-950/20 dark:border-yellow-900"
          : property.default?.includes('error') || property.default?.includes('❌')
          ? "bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-900"
          : property.default?.includes('success') || property.default?.includes('✅')
          ? "bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-900"
          : "bg-muted/50"
      )}>
        <div className="text-sm text-foreground whitespace-pre-wrap">
          {property.default || property.description || 'No information available'}
        </div>
      </div>
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  );
}

export default NoticeField;
